import { toast } from 'react-toastify';
import { IErrorModel, IPaginatedResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import returnsService from 'services/api/returns.service';

// -------------------------------------------------------------------------

interface IReturn {
    id: number;
    saleOrderItem: any;
    quantity: number;
    reasonStatus: string;
    reasonToOthers: string;
    status: string;
}

interface IReturnsResponse extends IPaginatedResponse {
    content: IReturn[];
}

interface IReturnState {
    loading: boolean;
    createLoading: boolean;
    acceptRejectLoading: boolean;
    error: IErrorModel | undefined;
    returns: IReturnsResponse | null;
}

const initialState: IReturnState = {
    loading: false,
    createLoading: false,
    acceptRejectLoading: false,
    error: {} as IErrorModel,
    returns: null,
};

// -------------------------------------------------------------------------

export const getReturnsThunk = createAsyncThunk<IReturnsResponse, { pageSize: number, pageNumber: number }, { rejectValue: IErrorModel }>(
    `/return/getReturns`,
    async ({ pageSize, pageNumber }, { rejectWithValue }: any) => {
        try {
            const response: any = await returnsService.getReturns({ pageSize, pageNumber });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch returns', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);


// -------------------------------------------------------------------------

export const acceptOrRejectReturnThunk = createAsyncThunk<any, { returnedItemId: number, accept: boolean }, { rejectValue: IErrorModel }>(
    `/return/acceptOrRejectReturn`,
    async ({ returnedItemId, accept }, { rejectWithValue }: any) => {
        try {
            const response: any = await returnsService.acceptOrRejectReturn(returnedItemId, accept);
            toast.success(response.data.message || 'Return accepted or rejected successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to accept or reject return', { autoClose: 5000 });
            return rejectWithValue(err);

        }
    }
);

// -------------------------------------------------------------------------


const returnSlice = createSlice({
    name: `return`,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Get Returns
        builder.addCase(getReturnsThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getReturnsThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.returns = action.payload;
        });
        builder.addCase(getReturnsThunk.rejected, (state, action) => {
            state.loading = false;
            state.returns = null;
            state.error = action.payload;
        });

        // Accept or Reject Return
        builder.addCase(acceptOrRejectReturnThunk.pending, (state) => {
            state.acceptRejectLoading = true;
        });
        builder.addCase(acceptOrRejectReturnThunk.fulfilled, (state) => {
            state.acceptRejectLoading = false;
        });
        builder.addCase(acceptOrRejectReturnThunk.rejected, (state, action) => {
            state.acceptRejectLoading = false;
            state.error = action.payload;
        });

    }
});

// -------------------------------------------------------------------------

export default returnSlice;
export const returnReducer = returnSlice.reducer;

